import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Radar, Line } from "react-chartjs-2";
import "chart.js/auto"; 

// Sample data imports
import { allSets as englishSets, hvacExtras as englishExtras } from "./hvacAssessmentData";
import { allSets as hindiSets, hvacExtras as hindiExtras } from "./hvacAssessmentDataHindi";
import useFetch from "../../hooks/useFetch";

export default function HVACSustainabilityAssessment() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [saveMessage, setSaveMessage] = useState(""); 
  const [saveStatus, setSaveStatus] = useState("");   
  const [isSaved, setIsSaved] = useState(false);    
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lineChartRef = useRef(null);
  const [language, setLanguage] = useState("en");
  const allSets = language === "en" ? englishSets : hindiSets;
  const hvacExtras = language === "en" ? englishExtras : hindiExtras;

  const [aiPrompt, setAiPrompt] = useState("");
  const [notification, setNotification] = useState(""); 
  const [notificationType, setNotificationType] = useState("info"); 
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSet, setPendingSet] = useState(null);

  const [fetchData, { loading }] = useFetch();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [selectedSet, setSelectedSet] = useState(1);
  const [activeTab, setActiveTab] = useState("start");
  const baseQuestions = allSets[selectedSet];

  // Answers state
  const initialAnswers = {};
  baseQuestions.forEach((q) => (initialAnswers[q.id] = 1));
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem("hvac_assessment_answers");
      return saved ? JSON.parse(saved) : initialAnswers;
    } catch (e) {
      return initialAnswers;
    }
  });

  const [includeExtras, setIncludeExtras] = useState(false);
  const [extraAnswers, setExtraAnswers] = useState(() => {
    const ia = {};
    hvacExtras.forEach((q) => (ia[q.id] = 1));
    return ia;
  });

  useEffect(() => {
    localStorage.setItem("hvac_assessment_answers", JSON.stringify(answers));
  }, [answers]);

  const handleChange = (id, value, isExtra = false) => {
    if (isExtra) {
      setExtraAnswers((s) => ({ ...s, [id]: Number(value) }));
    } else {
      setAnswers((s) => ({ ...s, [id]: Number(value) }));
    }
  };

  const totalScore = () => {
    const baseTotal = Object.values(answers).reduce((a, b) => a + b, 0);
    const extraTotal = includeExtras ? Object.values(extraAnswers).reduce((a, b) => a + b, 0) : 0;
    return baseTotal + extraTotal;
  };

  const maxScore = () => {
    const baseMax = baseQuestions.length * 5;
    const extraMax = includeExtras ? hvacExtras.length * 5 : 0;
    return baseMax + extraMax;
  };

  const levelFromScore = (score) => {
    const max = maxScore();
    const scaled = Math.round((score / max) * 75);
    if (scaled <= 25) return { level: 1, name: "Level 1 – Ad-hoc / Minimal Sustainability" };
    if (scaled <= 40) return { level: 2, name: "Level 2 – Emerging Practices" };
    if (scaled <= 55) return { level: 3, name: "Level 3 – Process-Driven" };
    if (scaled <= 65) return { level: 4, name: "Level 4 – Integrated & Monitored" };
    return { level: 5, name: "Level 5 – Predictive & Optimized" };
  };

  const getRecommendations = (level) => {
    const map = {
      1: [
        "Document key HVAC operations and maintenance processes.",
        "Begin basic energy monitoring and tracking.",
        "Train staff on basic sustainability practices.",
      ],
      2: [
        "Introduce SOPs for HVAC maintenance and cleaning schedules.",
        "Implement basic energy efficiency metrics.",
        "Monitor indoor air quality regularly.",
      ],
      3: [
        "Conduct energy audits and optimize equipment schedules.",
        "Adopt predictive maintenance practices.",
        "Integrate sustainability KPIs into reporting.",
      ],
      4: [
        "Automate monitoring and reporting systems.",
        "Benchmark against industry sustainability standards.",
        "Engage supply chain for energy-efficient practices.",
      ],
      5: [
        "Use AI for predictive optimization of HVAC energy usage.",
        "Lead in green certifications and energy excellence.",
        "Share insights for cluster-level sustainability initiatives.",
      ],
    };
    return map[level] || [];
  };

  const pillarScores = () => {
    const pillarMap = {};
    baseQuestions.forEach((q) => {
      pillarMap[q.pillar] = (pillarMap[q.pillar] || 0) + answers[q.id];
    });
    if (includeExtras) {
      hvacExtras.forEach((q) => {
        pillarMap[q.pillar] = (pillarMap[q.pillar] || 0) + extraAnswers[q.id];
      });
    }

    const pillarCounts = {};
    baseQuestions.forEach((q) => (pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1));
    if (includeExtras) hvacExtras.forEach((q) => (pillarCounts[q.pillar] = (pillarCounts[q.pillar] || 0) + 1));

    return Object.keys(pillarMap).map((p) => ({
      subject: p,
      A: +(pillarMap[p] / pillarCounts[p]).toFixed(2),
      fullMark: 5,
    }));
  };

  const resetAll = () => {
    const ia = {};
    baseQuestions.forEach((q) => (ia[q.id] = 1));
    setAnswers(ia);
    const ea = {};
    hvacExtras.forEach((q) => (ea[q.id] = 1));
    setExtraAnswers(ea);
    setIncludeExtras(false);
    localStorage.removeItem("hvac_assessment_answers");
  };

  const exportJSON = () => {
    const payload = {
      answers,
      extraAnswers: includeExtras ? extraAnswers : null,
      total: totalScore(),
      max: maxScore(),
      level: levelFromScore(totalScore()),
      date: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hvac_sustainability_assessment.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const score = totalScore();
  const levelObj = levelFromScore(score);
  const recs = getRecommendations(levelObj.level);
  const chartData = pillarScores();
  const labels = chartData.map((d) => d.subject);
  const dataPoints = chartData.map((d) => d.A);

  const radarData = {
    labels,
    datasets: [
      {
        label: "Average Score",
        data: dataPoints,
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        borderColor: "#10b981",
        borderWidth: 2,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#10b981",
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: { beginAtZero: true, max: 5, ticks: { stepSize: 1 } },
    },
  };

  // Placeholder history and trend data (can be replaced with API)
  const dummyHistory = [
    { id: 1, set_id: 1, date: "2025-01-01", level: "Beginner", score: 18, total: 25, percent: 72 },
    { id: 2, set_id: 1, date: "2025-01-15", level: "Intermediate", score: 22, total: 25, percent: 88 },
    { id: 3, set_id: 2, date: "2025-02-01", level: "Beginner", score: 19, total: 25, percent: 76 },
  ];

  // AI prompt generation
  const handleGeneratePrompt = () => {
    const allAnswers = baseQuestions.map((q) => `Q: ${q.label}\nA: ${answers[q.id] || "No answer"}`);
    if (includeExtras) {
      hvacExtras.forEach((q) => allAnswers.push(`Q: ${q.label}\nA: ${extraAnswers[q.id] || "No answer"}`));
    }

    const prompt = `
      You are a sustainability consultant AI. Based on HVAC Sustainability Self Assessment:
      Set: ${selectedSet}, Score: ${score}/${maxScore()}, Level: ${levelObj.name}
      Recommendations: ${recs.join(", ")}
      User Answers:
      ${allAnswers.join("\n")}
      Generate 10 actionable suggestions to improve HVAC sustainability.
    `;
    setAiPrompt(prompt);
    setNotification("AI Prompt generated! You can ask Thinky AI now.");
    setNotificationType("success");
  };

  const handleAskThinkyAI = () => {
    if (!aiPrompt) {
      setNotification("Please generate AI Prompt first!");
      setNotificationType("error");
      return;
    }
    navigate("/agenticsim", { state: { prompt: aiPrompt } });
  };

  const handleCTA = () => navigate("/tasks/add");

  // Save handler (simulate API)
  const handleSave = async () => {
    const formData = { answers, extraAnswers, selectedSet, includeExtras, score, level: levelObj.name };
    try {
      // Replace with your actual API
      setSaveMessage("Saved successfully!");
      setSaveStatus("success");
      setIsSaved(true);
    } catch {
      setSaveMessage("Error saving data!");
      setSaveStatus("error");
    }
    setTimeout(() => setSaveMessage(""), 3000);
  };

  const handleShare = () => {
    const textToShare = `Self Assessment – Set ${selectedSet}\nScore: ${score} / ${maxScore()}\nStage: ${levelObj.name}\nRecommendations:\n${recs.join("\n")}`;

    if (navigator.share) {
      // Mobile native share
      navigator
        .share({
          title: `Assessment Set ${selectedSet}`,
          text: textToShare,
        })
        .then(() => console.log("Shared successfully"))
        .catch((err) => console.error("Share failed:", err));
    } else {
      // Fallback: WhatsApp share
      const whatsappURL = `https://api.whatsapp.com/send?text=${encodeURIComponent(textToShare)}`;
      window.open(whatsappURL, "_blank");
    }
  };

  return (
    <div className="bg-black min-h-screen p-4">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 p-3 flex justify-between items-center">
        <div className="text-yellow-200 font-bold text-xl">Think5Whys HVAC Sustainability Assessment</div>
        <div className="flex gap-2 items-center">
          {isLoggedIn ? (
            <>
              <div className="text-teal-300 text-xs">{userName}</div>
              <button onClick={handleLogout} className="bg-red-600 w-8 p-2 rounded">⏻</button>
            </>
          ) : (
            <Link to="/login" className="text-teal-300 w-8">🔐</Link>
          )}
          <button onClick={() => setIsMobileMenuOpen((s) => !s)} className="w-8 lg:hidden text-teal-300 text-xl">
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-4 mb-4 text-sm max-w-5xl mx-auto">
        <button onClick={() => setActiveTab("start")} className={`py-2 rounded text-white border ${activeTab==="start"?"bg-blue-600 border-blue-400":"bg-slate-800 border-slate-600"}`}>Start Assessment</button>
        <button onClick={() => setActiveTab("history")} className={`py-2 rounded text-white border ${activeTab==="history"?"bg-blue-600 border-blue-400":"bg-slate-800 border-slate-600"}`}>History</button>
        <button onClick={() => setActiveTab("trend")} className={`py-2 rounded text-white border ${activeTab==="trend"?"bg-blue-600 border-blue-400":"bg-slate-800 border-slate-600"}`}>Trend</button>
        <select value={language} onChange={(e)=>setLanguage(e.target.value)} className="h-10 bg-slate-800 text-white border border-slate-600 rounded">
          <option value="en">English</option>
          <option value="hi">Hindi</option>
        </select>
      </div>

      {activeTab==="start" && (
        <div className="max-w-5xl mx-auto">
          <div className="mb-4 text-white">
            <div>Total Score: {score} / {maxScore()}</div>
            <div>Level: {levelObj.name}</div>
            <button onClick={resetAll} className="bg-emerald-500 text-white px-3 py-1 w-40 rounded mt-2">Reset</button>
            <button onClick={exportJSON} className="bg-emerald-500 text-white px-3 py-1 w-40 rounded mt-2 ml-2">Export JSON</button>
          </div>

          <form className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {baseQuestions.map((q)=>(
              <div key={q.id} className="bg-black border border-blue-500 p-4 rounded text-white">
                <label>{q.label}</label>
                <div className="flex gap-2 mt-1">
                  {[1,2,3,4,5].map(n=>(
                    <label key={n}>
                      <input type="radio" name={q.id} value={n} checked={answers[q.id]===n} onChange={e=>handleChange(q.id,e.target.value)} className="mr-1"/>
                      {n}
                    </label>
                  ))}
                </div>
                <div className="text-xs mt-1">Pillar: {q.pillar}</div>
              </div>
            ))}

            <div className="lg:col-span-2 flex items-center gap-2 text-white">
              <input type="checkbox" checked={includeExtras} onChange={e=>setIncludeExtras(e.target.checked)}/> Include Extra HVAC Questions
            </div>

            {includeExtras && hvacExtras.map(q=>(
              <div key={q.id} className="bg-black border border-blue-500 p-4 rounded text-white">
                <label>{q.label}</label>
                <div className="flex gap-2 mt-1">
                  {[1,2,3,4,5].map(n=>(
                    <label key={n}>
                      <input type="radio" name={q.id} value={n} checked={extraAnswers[q.id]===n} onChange={e=>handleChange(q.id,e.target.value,true)} className="mr-1"/>
                      {n}
                    </label>
                  ))}
                </div>
                <div className="text-xs mt-1">Pillar: {q.pillar}</div>
              </div>
            ))}
          </form>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white p-4 rounded">
              <h3>Pillar Radar</h3>
              <div className="h-72">
                <Radar data={radarData} options={radarOptions}/>
              </div>
            </div>
            <div className="bg-black text-white p-4 rounded border border-cyan-200">
              <h3 className="text-white">Recommendations</h3>
              <ul className="list-disc pl-5">
                {recs.map((r,i)=><li className="text-white" key={i}>{r}</li>)}
              </ul>
              {/* <div className="flex flex-col gap-2 mt-4">
                <button onClick={handleSave} className="bg-blue-500 px-3 py-1 rounded">💾 Save</button> */}
               <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2 mt-4 place-items-center">
                  <button
                    onClick={() => window.print()}
                    className="bg-blue-500 text-sm px-3 py-1 rounded border w-full"
                  >
                    🖨️ Print
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={loading || isSaved}
                    className={`text-sm px-3 py-1 rounded border flex justify-center items-center gap-1 w-full ${
                      isSaved ? "bg-gray-500 text-gray-200 cursor-not-allowed" : "bg-blue-500 text-white"
                    }`}
                  >
                    {loading ? "Saving..." : isSaved ? "Saved ✅" : "💾 Save"}
                  </button>

                  <button
                    onClick={handleShare}
                    className="bg-gray-400 text-sm px-3 py-1 rounded border w-full"
                  >
                    📤 Share
                  </button>
                </div>
              {/* </div> */}
            </div>
            <div className="flex flex-row gap-2 mt-4">
                <button onClick={handleGeneratePrompt} className="bg-yellow-500 text-sm px-1 py-1 rounded text-black w-40 lg:w-64">🤖 AI Prompt</button>
                <button onClick={handleAskThinkyAI} className="bg-green-500 text-sm px-1 py-1 rounded w-40 lg:w-80">🧠 Ask Thinky AI</button>
                <button onClick={handleCTA} className="bg-purple-500 text-sm px-1 py-1 rounded w-40 lg:w-40">📝 CTA</button>
            </div>
          </div>
          {notification && <div className="mt-2 p-2 rounded bg-green-600">{notification}</div>}
          {aiPrompt && <textarea className="mt-2 w-full p-2 bg-gray-900 text-white">{aiPrompt}</textarea>}

        </div>
        
      )}

      {activeTab==="history" && (
        <div className="text-white max-w-5xl mx-auto">
          <h2 className="text-xl font-bold mb-4">Assessment History</h2>
          <table className="w-full border border-gray-400">
            <thead>
              <tr className="bg-gray-800">
                <th className="border px-2">Date</th>
                <th className="border px-2">Level</th>
                <th className="border px-2">Score</th>
                <th className="border px-2">Percent</th>
              </tr>
            </thead>
            <tbody>
              {dummyHistory.map((h)=>(
                <tr key={h.id}>
                  <td className="border px-2">{h.date}</td>
                  <td className="border px-2">{h.level}</td>
                  <td className="border px-2">{h.score}/{h.total}</td>
                  <td className="border px-2">{h.percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab==="trend" && (
        <div className="text-white max-w-5xl mx-auto">
          <h2 className="text-xl font-bold mb-4">Trend Chart</h2>
          <Line
            ref={lineChartRef}
            data={{
              labels: dummyHistory.map(h=>h.date),
              datasets: [{
                label: "Score %",
                data: dummyHistory.map(h=>h.percent),
                borderColor: "#10b981",
                backgroundColor: "rgba(16, 185, 129, 0.2)",
                tension: 0.3,
              }]
            }}
          />
        </div>
      )}
    </div>
  );
}
